/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useAuth, useCurrentOrg, useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { AdminChromeTopNavTabs } from '@kaiizen/kai-shiki/react';
import { Navigate, useLocation, useParams } from 'react-router';

import { AdminChromePageTitleBar } from '@/Client/Layout/AdminChromePageTitleBar';
import { useGuardedNavigate } from '@/Client/State/NavigationGuardContext';
import {
  NotificationBell,
  ProfileDropdownMenu,
  SyncingStatusIndicator,
} from '@/Client/Widgets';
import { ApiKeysTabContent } from './ApiKeysTabContent';
import { InputsTabContent } from './InputsTabContent';
import { NotificationsTabContent } from './NotificationsTabContent';
import {
  ORG_SETTINGS_COLORS,
  ORG_SETTINGS_PAGE_BODY_STYLE,
  ORG_SETTINGS_PAGE_TITLE_STYLE,
  ORG_SETTINGS_SPACING,
} from './OrgSettingsStyleLib';
import { OutputsTabContent } from './OutputsTabContent';
import { WorkflowTabContent } from './WorkflowTabContent';

const PAGE_STYLE = css`
  label: PAGE_STYLE;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${ORG_SETTINGS_COLORS.pageBackground};
  overflow: hidden;
`;

const CONTENT_STYLE = css`
  label: CONTENT_STYLE;
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: ${ORG_SETTINGS_SPACING.standardPadding}px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const REDESIGN_CONTENT_WRAPPER_STYLE = css`
  label: REDESIGN_CONTENT_WRAPPER_STYLE;
  padding: 0 0 ${ORG_SETTINGS_SPACING.standardPadding}px;
`;

const CARD_STYLE = css`
  label: CARD_STYLE;
  background: ${ORG_SETTINGS_COLORS.white};
  border: 1px solid ${ORG_SETTINGS_COLORS.border};
  padding: ${ORG_SETTINGS_SPACING.standardPadding}px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 960px;
`;

const SECTION_TITLE_STYLE = css`
  label: SECTION_TITLE_STYLE;
  ${ORG_SETTINGS_PAGE_TITLE_STYLE}
`;

const BODY_STYLE = css`
  label: BODY_STYLE;
  ${ORG_SETTINGS_PAGE_BODY_STYLE}
`;

const ALL_ORG_SETTINGS_SECTIONS = [
  { key: 'inputs', label: 'Inputs', path: '/org-settings/inputs' },
  { key: 'workflow', label: 'Workflow', path: '/org-settings/workflow' },
  {
    key: 'notifications',
    label: 'Notifications',
    path: '/org-settings/notifications',
  },
  { key: 'outputs', label: 'Outputs', path: '/org-settings/outputs' },
  { key: 'api-keys', label: 'API Keys', path: '/org-settings/api-keys' },
] as const;

type OrgSettingsSectionKey = (typeof ALL_ORG_SETTINGS_SECTIONS)[number]['key'];

const isOrgSettingsSectionKey = (
  value: string | undefined,
): value is OrgSettingsSectionKey => ALL_ORG_SETTINGS_SECTIONS.some(section => section.key === value);

type OrgSettingsSection = (typeof ALL_ORG_SETTINGS_SECTIONS)[number];

const getAccessibleSections = ({ apiKeysEnabled }: { apiKeysEnabled: boolean }) =>
  ALL_ORG_SETTINGS_SECTIONS.filter(section => section.key !== 'api-keys' || apiKeysEnabled);

const OrgSettingsSectionTabs = ({
  sections,
  pathname,
  onNavigate,
}: {
  sections: readonly OrgSettingsSection[];
  pathname: string;
  onNavigate: (path: string) => void;
}) => (
  <AdminChromeTopNavTabs
    ariaLabel='Org settings sections'
    items={sections.map(section => ({
      label: section.label,
      selected: pathname === section.path,
      onClick: () => onNavigate(section.path),
    }))}
  />
);

export const OrgSettingsPage = () => {
  const { section } = useParams<{ section?: string }>();
  const { pathname } = useLocation();
  /* Still guard-aware although no section registers a guard any more: every tab autosaves, so
     there is nothing unsaved for one to protect (KAIDEV-4345). */
  const navigate = useGuardedNavigate();
  const { currentOrg, isLoading: isCurrentOrgLoading } = useCurrentOrg();
  const { isSuperAdmin } = useAuth();
  const { featureFlags, featureFlagsReady } = useFeatureFlags();
  const sections = getAccessibleSections({ apiKeysEnabled: !!featureFlags.apiKeys });
  const fallbackSectionPath = sections[0]?.path ?? '/account';
  const canAccessOrgSettings = currentOrg?.role === 'admin' || isSuperAdmin === true;

  if (isCurrentOrgLoading || featureFlagsReady === false) {
    return null;
  }

  if (!canAccessOrgSettings) {
    return <Navigate to='/account' replace />;
  }

  /* Modifiers moved into the Workflow page, so an old link to it belongs there rather than at
     whichever section happens to come first (KAIDEV-4345). */
  if (section === 'modifiers') {
    return <Navigate to='/org-settings/workflow' replace />;
  }

  if (!isOrgSettingsSectionKey(section) || !sections.some(currentSection => currentSection.key === section)) {
    return <Navigate to={fallbackSectionPath} replace />;
  }

  return (
    <div css={PAGE_STYLE}>
      <AdminChromePageTitleBar
        title='Org Settings'
        headerButtons={
          <OrgSettingsSectionTabs
            sections={sections}
            pathname={pathname}
            onNavigate={navigate}
          />
        }
        orgLabel={currentOrg?.name || ''}
        notifications={<NotificationBell />}
        syncStatus={<SyncingStatusIndicator />}
        profile={<ProfileDropdownMenu />}
      />

      <div css={[CONTENT_STYLE, REDESIGN_CONTENT_WRAPPER_STYLE]}>
        {section === 'inputs'
          ? <InputsTabContent />
          : section === 'workflow'
          ? <WorkflowTabContent />
          : section === 'notifications'
          ? <NotificationsTabContent />
          : section === 'outputs'
          ? <OutputsTabContent />
          : section === 'api-keys'
          ? <ApiKeysTabContent />
          : (
            <div css={CARD_STYLE}>
              <div css={SECTION_TITLE_STYLE}>Org Settings</div>
              <div css={BODY_STYLE}>Section unavailable.</div>
            </div>
          )}
      </div>
    </div>
  );
};
